import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Check, Loader2, Save, ArrowLeftRight, Copy, AlertTriangle, Pencil, X } from 'lucide-react';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Dialog, DialogContent, DialogTitle } from '../ui/dialog';
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter, AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel } from '../ui/alert-dialog';
import { InvoiceViewer } from './InvoiceViewer';
import { DuplicateCompareDialog } from './DuplicateCompareDialog';
import { LineItemsTable, toPence, fmtGBP } from './LineItemsTable';
import { AiReadStatus, InvoiceHeaderFields } from './InvoiceHeaderFields';
import { DeleteInvoiceButton, HoldButton, ReopenButton } from './InboxActions';
import { LiveCostsStatus, SendToLiveCostsButton, XeroStatus, SendToXeroButton, CheckDeliveryButton } from './LiveCostsActions';
import { CoverCapturesPanel } from './CoverCapturesPanel';
import { TeachReaderPanel } from './TeachReaderPanel';
import { SupplierQueryPanel } from './SupplierQueryPanel';
import { CisPanel } from './CisPanel';
import { defaultCis, CIS_CATEGORIES } from './cisRules';
import { STATUS_BADGE, STATUS_LABEL, isReading, fmtUKDate } from './inboxFormat';
import { recalcVat, lineRateIssue, totalsSourceFor, totalModeFor, moneyInput } from './vatRules';
import { SendAsBlock, GeneratedPreview, DownloadGeneratedButton, DocTabs } from './GeneratedInvoice';
import { SplitButton, SplitChildBanner, SplitParentBanner } from './SplitByCompany';
import { VoidDeleteButton } from './VoidDelete';
import { StatementBanner, MarkStatementButton, StatementHint } from './Statements';

// Net comes from net_pence ONLY — never from the gross. Unread VAT stays blank (never assumed £0).
const toForm = (it) => ({
  supplier_name: it.supplier_name || '', reference: it.reference || '', invoice_date: it.invoice_date || '',
  net: it.net_pence != null && it.net_pence > 0 ? moneyInput(it.net_pence) : '',
  vat: it.vat_pence != null ? moneyInput(it.vat_pence) : '',
  vat_status: it.vat_status || (it.vat_pence == null ? 'unknown' : 'ok'),
  vat_note: it.vat_note || '',
  billed_to_company: it.billed_to_company || '',
  send_as: it.split ? 'generated' : (it.send_as || 'original'),
  cis: defaultCis(it),
});
// CIS invoices carry no VAT (not registered) or reverse-charge VAT at 0% — the VAT never follows the net.
const vatAfterNet = (netPence, f, lines, item) => (f.cis?.enabled ? { vat: '0.00', vat_status: 'ok', vat_note: '' } : recalcVat(netPence, f, lines, item));
const toLines = (it) => (it.lines || []).map(l => ({
  id: l.id, project_id: l.project_id || '', no_project: !!l.no_project, category: l.category || '', description: l.description || '',
  amount: l.value_pence ? (l.value_pence / 100).toFixed(2) : '', vat_rate: l.vat_rate ?? null, vat_source: l.vat_source || null, suggested: !!l.suggested, source: l.source || 'ai',
}));
const sumLines = (ls) => ls.reduce((s, l) => s + toPence(l.amount), 0);

/** Full-screen review: invoice on the left (zoom / pan / pages), header + lines on the right.
 *  A posted invoice opens read-only; "Edit invoice" (or `startEditing`) re-opens the same
 *  pane for a full or partial correction — ledger rows are updated in place, dropped lines voided. */
export function ProcessDialog({ api, itemId, projects, onClose, onChanged, reloadProjects, startEditing = false, onOpenItem }) {
  const [item, setItem] = useState(null);
  const [editing, setEditing] = useState(!!startEditing);
  const [editReason, setEditReason] = useState('');
  const [docTab, setDocTab] = useState('original');
  const [extraProjects, setExtraProjects] = useState([]);
  const allProjects = useMemo(() => {
    const ids = new Set(projects.map(p => p.id));
    return [...projects, ...extraProjects.filter(p => !ids.has(p.id))];
  }, [projects, extraProjects]);
  const onAdhocCreated = useCallback((proj) => {
    setExtraProjects(prev => [...prev.filter(p => p.id !== proj.id), proj]);
    reloadProjects?.();
  }, [reloadProjects]);
  const [form, setFormRaw] = useState(toForm({}));
  const [lines, setLinesRaw] = useState([]);
  const [busy, setBusy] = useState(false);
  const [rereading, setRereading] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [totalMode, setTotalMode] = useState('imported'); // imported (AI/saved) | auto (follows lines) | manual (typed)
  const [linesVersion, setLinesVersion] = useState(0);
  const [coverIds, setCoverIds] = useState([]);
  const [dupConfirm, setDupConfirm] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const dirtyRef = useRef(false);
  const setForm = (u) => { dirtyRef.current = true; setDirty(true); setFormRaw(u); };
  // Net only follows the lines when their total actually changes (a project/category fix leaves it alone).
  const setLines = (u) => {
    dirtyRef.current = true; setDirty(true);
    setLinesRaw(prev => {
      const next = typeof u === 'function' ? u(prev) : u;
      if (sumLines(next) !== sumLines(prev)) setTotalMode(m => (m === 'manual' ? m : 'auto'));
      return next;
    });
  };

  const apply = useCallback((it, force = false) => {
    setItem(it);
    if (force || !dirtyRef.current) {
      setFormRaw(toForm(it)); setLinesRaw(toLines(it));
      dirtyRef.current = false; setDirty(false); setTotalMode(totalModeFor(it)); setLinesVersion(v => v + 1);
    }
  }, []);

  useEffect(() => {
    let alive = true; let timer;
    const tick = async () => {
      try {
        const r = await api.get(`/cost-inbox/${itemId}`);
        if (!alive) return;
        apply(r.data);
        if (isReading(r.data) || (r.data.status === 'posted' && r.data.livecosts?.status === 'pending')) timer = setTimeout(tick, 2500);
      } catch (e) { if (alive) toast.error(e.response?.data?.detail || 'Failed to load invoice'); }
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, [api, itemId, apply]);

  const posted = item?.status === 'posted';
  const split = item?.status === 'split';
  const statement = item?.status === 'statement';
  const readOnly = !item || item.status === 'rejected' || split || statement || (posted && !editing);
  const netPence = toPence(form.net), vatKnown = form.vat !== '', vatPence = toPence(form.vat), grossPence = netPence + vatPence;
  const linesTotal = sumLines(lines);
  // Net changed (typed, "Match lines" or following the lines): VAT is recalculated at the invoice's known rate and flagged to check.
  const changeNet = (value, mode) => {
    setTotalMode(mode);
    setForm(f => {
      const next = toPence(value);
      return next === toPence(f.net) ? { ...f, net: value } : { ...f, net: value, ...vatAfterNet(next, f, lines, item) };
    });
  };
  useEffect(() => {
    if (totalMode !== 'auto') return;
    setFormRaw(f => {
      if (linesTotal === toPence(f.net)) return f;
      const net = linesTotal > 0 ? moneyInput(linesTotal) : '';
      return { ...f, net, ...vatAfterNet(linesTotal, f, lines, item) };
    });
  }, [totalMode, linesTotal, lines, item]);
  const rateIssue = vatKnown ? lineRateIssue(lines, vatPence) : null;
  const linesValid = lines.length > 0 && lines.every(l => (l.project_id || l.no_project) && l.category && toPence(l.amount) > 0);
  const cisOn = !!form.cis?.enabled;
  // A saved CIS invoice whose lines still carry a VAT rate (e.g. 20% from the reader) → zero them on load.
  useEffect(() => {
    if (!cisOn || readOnly) return;
    if (lines.some(l => l.vat_rate != null && l.vat_rate !== '' && Number(l.vat_rate) !== 0)) {
      setLines(ls => ls.map(l => (l.vat_rate != null && l.vat_rate !== '' && Number(l.vat_rate) !== 0) ? { ...l, vat_rate: '0', vat_source: 'cis' } : l));
    }
  }, [cisOn, linesVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  const cisCategoryOk = !cisOn || lines.every(l => !l.category || CIS_CATEGORIES.includes(l.category));
  const canPost = !readOnly && linesValid && netPence > 0 && vatKnown && vatPence >= 0 && linesTotal === netPence && !rateIssue && cisCategoryOk;
  const postBlocker = !vatKnown ? "Enter the VAT from the invoice (0 if there's none)" : rateIssue || (!cisCategoryOk ? 'CIS invoices use Labour, Materials or Fuel lines only' : 'Every line needs a project, category and amount, and the lines (ex VAT) must add up to the invoice net total');
  const ai = item?.ai_totals;
  const aiMatches = !!ai && Math.round((ai.net_pounds ?? -1) * 100) === netPence && (ai.vat_pounds == null ? !vatKnown : Math.round(ai.vat_pounds * 100) === vatPence);
  const useAiTotals = () => {
    setTotalMode('imported');
    setForm(f => ({ ...f, net: ai.net_pounds != null ? moneyInput(Math.round(ai.net_pounds * 100)) : '',
      ...(f.cis?.enabled ? { vat: '0.00', vat_status: 'ok', vat_note: '' } : {
        vat: ai.vat_pounds != null ? moneyInput(Math.round(ai.vat_pounds * 100)) : '',
        vat_status: ai.vat_pounds == null ? 'unknown' : ai.reconciles ? 'ok' : 'check', vat_note: ai.reconciles ? '' : "The reader's Net + VAT doesn't equal its Gross — check the totals against the invoice" }) }));
  };

  // "Billed to" = the LUX company that owes the supplier. Default order: an
  // explicit admin choice → the company the AI read from the invoice's bill-to →
  // the company most lines belong to (falls back to LUX Developments). The admin
  // can always override. The whole invoice is emailed to this company's Xero /
  // LiveCosts; lines on the other company's jobs are flagged to rebill.
  const projCompany = (pid) => ((allProjects.find(x => x.id === pid)?.project_ref || '').toUpperCase().startsWith('LC') ? 'CON' : 'DEV');
  const lineCos = lines.filter(l => l.project_id).map(l => projCompany(l.project_id));
  const derivedBilled = lineCos.length ? (lineCos.filter(c => c === 'CON').length > lineCos.filter(c => c === 'DEV').length ? 'CON' : 'DEV') : 'DEV';
  const billedTo = form.billed_to_company || item?.ai_billed_to_company || derivedBilled;
  const rebillCount = lineCos.filter(c => c !== billedTo).length;
  const setBilledTo = (v) => setForm(f => ({ ...f, billed_to_company: v }));
  const sendAs = readOnly ? (item?.send_as || 'original') : (form.send_as || 'original');
  const generatedOn = !!item && !split && sendAs === 'generated';
  const setSendAs = (v) => { setForm(f => ({ ...f, send_as: v })); setDocTab(v === 'generated' ? 'generated' : 'original'); };
  useEffect(() => { if (item?.send_as === 'generated') setDocTab('generated'); }, [item?.id, item?.send_as]);

  const payload = () => ({
    supplier_name: form.supplier_name || null, reference: form.reference || null, invoice_date: form.invoice_date || null,
    billed_to_company: billedTo,
    send_as: form.send_as || 'original',
    net_pounds: form.net === '' ? null : netPence / 100,
    vat_pounds: vatKnown ? vatPence / 100 : null,
    totals_source: totalsSourceFor(totalMode, aiMatches),
    vat_status: vatKnown ? form.vat_status : 'unknown',
    vat_note: form.vat_note || null,
    cis: { enabled: cisOn, rate: form.cis?.rate ?? 20, vat_treatment: form.cis?.vat_treatment || 'no_vat' },
    lines: lines.map(l => ({ id: String(l.id).startsWith('tmp-') ? undefined : l.id, project_id: l.no_project ? null : (l.project_id || null), no_project: !!l.no_project, category: l.category || null, description: l.description || null, value_pounds: toPence(l.amount) / 100, vat_rate: l.vat_rate ?? null, vat_source: l.vat_source || null })),
  });

  const save = async (quiet = false) => {
    const r = await api.patch(`/cost-inbox/${itemId}`, { ...payload(), mark_in_review: true });
    apply(r.data, true);
    if (!quiet) toast.success('Saved');
    onChanged();
    return r.data;
  };
  const onSave = async () => { setBusy(true); try { await save(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); } finally { setBusy(false); } };

  const post = async (confirm = false) => {
    setBusy(true);
    try {
      const r = await api.post(`/cost-inbox/${itemId}/post`, { ...payload(), cover_capture_ids: coverIds, confirm_duplicate: confirm });
      const n = r.data?.line_count || 1;
      const cov = r.data?.covered_captures || 0;
      toast.success(`Posted ${n} ledger ${n === 1 ? 'row' : 'rows'}${cov ? `, covered ${cov} capture${cov === 1 ? '' : 's'}` : ''}${r.data?.xero?.pushed ? ' and pushed to Xero' : ''} · sending to LiveCosts`);
      setDupConfirm(null); onChanged(); onClose();
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d && typeof d === 'object' && d.code === 'possible_duplicate') {
        setDupConfirm(d.duplicates || []);
      } else {
        toast.error((typeof d === 'string' && d) || 'Failed to post');
      }
    } finally { setBusy(false); }
  };

  const reread = async () => {
    setBusy(true); setRereading(true);
    try {
      if (dirtyRef.current) await save(true);
      const r = await api.post(`/cost-inbox/${itemId}/extract`);
      apply(r.data, true);
      toast.success(r.data.extraction_status === 'done' ? 'Invoice re-read' : 'AI read failed');
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Re-read failed'); }
    finally { setBusy(false); setRereading(false); }
  };

  const finish = () => { onChanged(); onClose(); };

  const startEdit = () => { apply(item, true); setEditReason(''); setEditing(true); };
  const cancelEdit = () => { apply(item, true); setEditing(false); };
  const saveEdit = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/cost-inbox/${itemId}/edit-posted`, { ...payload(), reason: editReason || null });
      apply(r.data.item, true);
      setEditing(false);
      const bits = [r.data.updated ? `${r.data.updated} line${r.data.updated === 1 ? '' : 's'} updated` : null, r.data.added ? `${r.data.added} added` : null, r.data.removed ? `${r.data.removed} voided` : null].filter(Boolean);
      toast.success(`Invoice updated${bits.length ? ` · ${bits.join(', ')}` : ''}`);
      if (r.data.resend_needed) toast.warning(r.data.item?.xero_send?.failure_reason || 'Billed-to changed — send the invoice to the right company again', { duration: 12000 });
      onChanged();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error((typeof d === 'string' && d) || 'Could not save the changes');
    } finally { setBusy(false); }
  };

  return (
    <>
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent aria-describedby={undefined} className="bg-[#1E2530] border-white/10 text-white p-0 gap-0 w-[96vw] max-w-[96vw] h-[94vh] flex flex-col overflow-hidden" data-testid="inbox-process-dialog">
        <div className="flex items-center gap-3 px-5 py-3 border-b border-white/10 pr-12 shrink-0">
          <DialogTitle className="truncate text-base">{posted && editing ? 'Edit posted invoice' : readOnly ? 'Invoice' : 'Process invoice'} — {item?.original_filename || '…'}</DialogTitle>
          {item && <Badge variant="outline" className={STATUS_BADGE[item.status]} data-testid="inbox-dialog-status">{STATUS_LABEL[item.status] || item.status}</Badge>}
          {item?.status === 'on_hold' && item.hold_note && <span className="text-xs text-purple-300 truncate">— {item.hold_note}</span>}
          {item?.no_project && <Badge variant="outline" className="border-amber-500/40 text-amber-300 bg-amber-500/10" data-testid="inbox-dialog-no-project">No project</Badge>}
          {(item?.status === 'posted' ? item?.cis?.enabled : cisOn) && <Badge variant="outline" className="border-[#F5A623]/40 text-[#F5A623] bg-[#F5A623]/10" data-testid="inbox-dialog-cis">CIS</Badge>}
          {item?.status === 'posted' && <div className="ml-auto"><XeroStatus item={item} compact /></div>}
        </div>

        <div className="flex flex-col lg:flex-row flex-1 min-h-0">
          <div className="flex-1 min-w-0 min-h-[40vh] lg:min-h-0 lg:border-r border-white/10 flex flex-col">
            {item && generatedOn && <DocTabs tab={docTab} setTab={setDocTab} />}
            {item && (generatedOn && docTab === 'generated'
              ? <GeneratedPreview key={`${item.id}-${linesVersion}`} api={api} itemId={item.id} getPayload={payload} live={!readOnly || (posted && editing)} filename={`${form.supplier_name || 'invoice'} ${form.reference || ''} - generated.pdf`} />
              : <InvoiceViewer api={api} itemId={item.id} pageCount={item.page_count || 1} filename={item.original_filename} />)}
          </div>
          <div className="lg:w-[700px] shrink-0 overflow-y-auto p-4 space-y-4 border-t lg:border-t-0 border-white/10" data-testid="inbox-review-panel">
            {!item ? <div className="text-center py-10"><Loader2 className="w-5 h-5 animate-spin text-gray-400 mx-auto" /></div> : (
              <>
                <SplitParentBanner api={api} item={item} onOpenItem={onOpenItem} onDeleted={finish} onChanged={() => { onChanged(); api.get(`/cost-inbox/${itemId}`).then(r => apply(r.data, true)).catch(() => {}); }} />
                <SplitChildBanner item={item} onOpenItem={onOpenItem} />
                <StatementBanner api={api} item={item} onChanged={() => { onChanged(); api.get(`/cost-inbox/${itemId}`).then(r => apply(r.data, true)).catch(() => {}); }} />
                {!readOnly && !posted && <StatementHint item={item} />}
                {!readOnly && !posted && !item.split && <AiReadStatus item={item} dirty={dirty} busy={busy} reading={rereading} onApply={() => apply(item, true)} onReread={reread} />}
                {posted && editing && (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-200 flex items-start gap-2" data-testid="inbox-edit-banner">
                    <Pencil className="w-4 h-4 mt-0.5 shrink-0" />
                    <div><span className="font-medium">Editing a posted invoice.</span> Change anything — supplier, reference, date, Billed to, totals, CIS or the lines. Ledger rows are updated in place; lines you remove are voided (never deleted) and every change is audited. Nothing is re-sent to LiveCosts / Xero automatically.</div>
                  </div>
                )}
                {item.duplicates?.length > 0 && (
                  <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-2.5 text-xs text-amber-200 flex items-start gap-2" data-testid="inbox-dialog-dup-warning">
                    <Copy className="w-4 h-4 mt-0.5 shrink-0" />
                    <div className="min-w-0">
                      <div><span className="font-medium">Possible duplicate.</span> Same supplier, invoice number and value as {item.duplicates.length === 1 ? 'another invoice' : `${item.duplicates.length} other invoices`} ({item.duplicates.map(d => d.posted ? 'already posted' : 'waiting').join(', ')}).{!readOnly ? " You can still post — you'll be asked to confirm." : ''}</div>
                      <button type="button" onClick={() => setCompareOpen(true)} className="mt-1.5 inline-flex items-center gap-1.5 font-semibold text-amber-100 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 rounded px-2 py-1" data-testid="inbox-dup-compare-btn">
                        <Copy className="w-3.5 h-3.5" /> Review side by side
                      </button>
                    </div>
                  </div>
                )}
                <InvoiceHeaderFields form={form} setForm={setForm} aiTotals={item.ai_totals} readOnly={readOnly}
                  totalMode={totalMode} setTotalMode={setTotalMode} linesTotalPence={linesTotal} onNetChange={changeNet} rateIssue={rateIssue} cis={form.cis} />
                <CisPanel api={api} item={item} form={form} setForm={setForm} lines={lines} setLines={setLines} readOnly={readOnly} aiTotals={item.ai_totals} />
                <div className="rounded-lg border border-white/10 bg-white/[0.03] p-2.5" data-testid="inbox-billed-to-block">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div>
                      <div className="text-xs text-gray-300 font-medium">Billed to · sends to this company's Xero / LiveCosts</div>
                      <div className="text-[11px] text-gray-500">The account the order was placed on — the whole invoice goes here, whatever the jobs.</div>
                    </div>
                    {(readOnly || item.split) ? (
                      <span className="text-sm font-semibold text-white px-2.5 py-1 rounded bg-white/5 border border-white/10" title={item.split ? 'Fixed on a split copy — this is the share for this company' : ''} data-testid="inbox-billed-to-readonly">{billedTo === 'CON' ? 'LUX Construction' : 'LUX Developments'}</span>
                    ) : (
                      <div className="inline-flex rounded-md overflow-hidden border border-white/15" data-testid="inbox-billed-to-toggle">
                        {[{ k: 'DEV', l: 'LUX Developments', a: 'bg-[#F5A623] text-[#1E2530]' }, { k: 'CON', l: 'LUX Construction', a: 'bg-sky-500 text-white' }].map(o => (
                          <button key={o.k} type="button" onClick={() => setBilledTo(o.k)} className={`px-3 h-8 text-xs font-medium transition-colors ${billedTo === o.k ? o.a : 'bg-[#1E2530] text-gray-300 hover:bg-white/5'}`} data-testid={`inbox-billed-to-${o.k}`}>{o.l}</button>
                        ))}
                      </div>
                    )}
                  </div>
                  {rebillCount > 0 && !item.split && !split && (
                    <div className="mt-2 text-[11px] text-purple-300 inline-flex items-center gap-1.5 bg-purple-500/10 border border-purple-500/25 rounded px-2 py-1" data-testid="inbox-rebill-hint">
                      <ArrowLeftRight className="w-3 h-3 shrink-0" /> <span>{rebillCount} line{rebillCount === 1 ? '' : 's'} on the other company's jobs will be flagged to rebill — still costed to the right project.{!readOnly && <> Or <b>Split by company</b> below to send each company its own invoice.</>}</span>
                    </div>
                  )}
                </div>
                {!split && <SendAsBlock value={sendAs} onChange={setSendAs} readOnly={readOnly} item={item} />}
                <SupplierQueryPanel api={api} item={item}
                  onUpdated={(data) => setItem(it => ({ ...it, query: data.query }))}
                  onChanged={onChanged} />
                <LineItemsTable lines={lines} setLines={setLines} projects={allProjects} invoiceTotalPence={netPence} grossPence={grossPence} vatPence={vatPence} readOnly={readOnly}
                  aiVatPence={ai?.vat_pounds != null ? Math.round(ai.vat_pounds * 100) : null}
                  company={billedTo} api={api} onAdhocCreated={onAdhocCreated} cis={cisOn}
                  linesVersion={linesVersion} aiDiffers={!!ai && !aiMatches} onUseAiTotals={ai ? useAiTotals : undefined} />
                <CoverCapturesPanel api={api} itemId={itemId} lines={lines} form={form} readOnly={readOnly || posted} item={item} selected={coverIds} setSelected={setCoverIds} />
                {!readOnly && !posted && !item.split && <TeachReaderPanel api={api} itemId={itemId} supplierName={form.supplier_name} disabled={readOnly} />}
                {item.email_in && (
                  <div className="text-xs text-sky-200/90 bg-sky-500/10 border border-sky-400/25 rounded px-2.5 py-1.5" data-testid="inbox-email-in-info">
                    Emailed in {item.email_in.received_at ? new Date(item.email_in.received_at).toLocaleString('en-GB') : ''} by {item.email_in.from_name || item.email_in.from}{item.email_in.from_name ? ` (${item.email_in.from})` : ''}{item.email_in.subject ? ` · "${item.email_in.subject}"` : ''}{(item.email_in.attachments || []).length > 1 ? ` · ${item.email_in.attachments.length} attachments combined` : ''}
                  </div>
                )}
                {item.status === 'posted' && (
                  <div className="text-xs text-gray-400 space-y-1" data-testid="inbox-posted-info">
                    <div>Posted {item.posted_at ? new Date(item.posted_at).toLocaleString('en-GB') : ''}{item.posted_by_name ? ` by ${item.posted_by_name}` : ''} · {item.ledger_entry_ids?.length || 1} ledger row{(item.ledger_entry_ids?.length || 1) === 1 ? '' : 's'}{item.edited_at ? <span className="text-amber-300/80" data-testid="inbox-edited-info"> · edited {new Date(item.edited_at).toLocaleString('en-GB')}{item.edited_by_name ? ` by ${item.edited_by_name}` : ''}</span> : null}</div>
                    <LiveCostsStatus item={item} />
                    <XeroStatus item={item} />
                    {item.send_as === 'generated' && <div className="text-[#F5A623]/90" data-testid="inbox-posted-generated-note">Sent as a generated invoice built from the figures above — the original is kept on this record.</div>}
                  </div>
                )}
              </>
            )}
          </div>
        </div>

        <div className="border-t border-white/10 shrink-0">
          {item && !readOnly && !canPost && lines.length > 0 && (
            <div className="flex items-start gap-1.5 px-4 pt-2 text-xs text-amber-300" data-testid="inbox-post-blocker">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" /><span>{postBlocker}</span>
            </div>
          )}
          <div className="flex items-center gap-2 px-4 py-3">
          {item && !readOnly && !posted && !item.split && <DeleteInvoiceButton api={api} item={item} onDeleted={finish} />}
          {posted && editing && (
            <Input value={editReason} onChange={e => setEditReason(e.target.value)} placeholder="Reason for the change (optional, goes on the audit trail)"
              className="bg-[#1E2530] border-white/20 text-white h-9 max-w-md text-sm" data-testid="inbox-edit-reason" />
          )}
          <span className="flex-1" />
          {posted && editing ? (
            <>
              <Button variant="ghost" onClick={cancelEdit} disabled={busy} data-testid="inbox-edit-cancel-btn"><X className="w-4 h-4 mr-1.5" /> Cancel</Button>
              <Button onClick={saveEdit} disabled={busy || !canPost} title={canPost ? '' : postBlocker} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="inbox-edit-save-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />} Save changes
              </Button>
            </>
          ) : readOnly ? (
            <>
              {item && <VoidDeleteButton api={api} item={item} onDone={finish} />}
              {item && <DownloadGeneratedButton api={api} item={item} />}
              {item && <SendToLiveCostsButton api={api} item={item} onSent={(lc) => { setItem(it => ({ ...it, livecosts: lc })); onChanged(); }} />}
              {item && <SendToXeroButton api={api} item={item} onSent={(xs) => { setItem(it => ({ ...it, xero_send: xs })); onChanged(); }} />}
              {item && <CheckDeliveryButton api={api} item={item} onChecked={(fresh) => { if (fresh) apply(fresh, true); onChanged(); }} />}
              {posted && <Button variant="outline" onClick={startEdit} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="inbox-edit-btn"><Pencil className="w-4 h-4 mr-1.5" /> Edit invoice</Button>}
              <Button variant="ghost" onClick={onClose} data-testid="inbox-close-btn">Close</Button>
            </>
          ) : item && (
            <>
              {item.status === 'on_hold'
                ? <ReopenButton api={api} item={item} busy={busy} onReopened={finish} />
                : <HoldButton api={api} item={item} busy={busy} beforeHold={() => dirtyRef.current && save(true)} onHeld={finish} />}
              {!item.split && <MarkStatementButton api={api} item={item} supplierName={form.supplier_name} busy={busy} beforeMark={() => dirtyRef.current && save(true)} onMarked={finish} />}
              {!item.split && lines.length > 1 && (
                <SplitButton api={api} itemId={itemId} lines={lines} projCompany={projCompany} billedTo={billedTo} getPayload={payload} vatPence={vatKnown ? vatPence : null}
                  disabled={busy || !linesValid || (netPence > 0 && linesTotal !== netPence)}
                  onSplit={(r) => { onChanged(); const first = r.children?.[0]; if (first && onOpenItem) onOpenItem(first.id); else onClose(); }} />
              )}
              <Button variant="outline" onClick={onSave} disabled={busy || !dirty} className="border-white/20 text-white bg-white/5 hover:bg-white/10" data-testid="inbox-save-btn">
                <Save className="w-4 h-4 mr-2" /> Save
              </Button>
              <Button onClick={() => post()} disabled={busy || !canPost} title={canPost ? '' : postBlocker} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D] font-semibold" data-testid="inbox-post-btn">
                {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />} Post {lines.length > 1 ? `${lines.length} lines` : ''} to ledger
              </Button>
            </>
          )}
          </div>
        </div>
      </DialogContent>
    </Dialog>

    <AlertDialog open={dupConfirm !== null} onOpenChange={(o) => { if (!o) setDupConfirm(null); }}>
      <AlertDialogContent className="bg-[#1E2530] border-white/10 text-white" data-testid="inbox-dup-confirm">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-400" /> Possible duplicate</AlertDialogTitle>
          <AlertDialogDescription className="text-gray-400">
            This invoice matches the supplier, invoice number and value of {(dupConfirm?.length || 0) === 1 ? 'an invoice' : `${dupConfirm?.length} invoices`} already in the system. Only post if this is genuinely a separate invoice.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2 max-h-60 overflow-auto text-xs">
          {(dupConfirm || []).map(d => (
            <div key={d.id} className="rounded border border-white/10 p-2" data-testid={`inbox-dup-confirm-item-${d.id}`}>
              <div className="font-medium text-gray-100">{d.supplier_name || d.original_filename || 'invoice'}</div>
              <div className="text-gray-400">Ref {d.reference || '—'} · {typeof d.value_pence === 'number' ? fmtGBP(d.value_pence) : '—'}{d.invoice_date ? ` · ${fmtUKDate(d.invoice_date)}` : ''} · {d.posted ? 'already posted' : 'waiting in inbox'}</div>
            </div>
          ))}
        </div>
        <AlertDialogFooter>
          <button type="button" onClick={() => setCompareOpen(true)} className="inline-flex items-center gap-1.5 text-sm font-medium text-amber-200 bg-amber-500/15 hover:bg-amber-500/25 border border-amber-500/40 rounded-md px-3 py-1.5 mr-auto" data-testid="inbox-dup-confirm-compare-btn">
            <Copy className="w-4 h-4" /> Review side by side
          </button>
          <AlertDialogCancel className="bg-white/5 border-white/20 text-white hover:bg-white/10" data-testid="inbox-dup-cancel">Cancel</AlertDialogCancel>
          <AlertDialogAction onClick={() => post(true)} className="bg-[#F5A623] text-[#1E2530] hover:bg-[#D98E1D]" data-testid="inbox-dup-post-anyway">Post anyway</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    {compareOpen && item && (
      <DuplicateCompareDialog
        api={api}
        current={{
          id: item.id,
          page_count: item.page_count || 1,
          filename: item.original_filename,
          supplier_name: form.supplier_name,
          reference: form.reference,
          invoice_date: form.invoice_date,
          value_pence: grossPence || netPence || item.value_pence || null,
        }}
        duplicates={(dupConfirm && dupConfirm.length ? dupConfirm : item.duplicates) || []}
        onClose={() => setCompareOpen(false)}
      />
    )}
    </>
  );
}
